import {
  Button,
  constants,
  FloppyHollowSVG,
  useWindowSize,
} from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";

import { Assignment } from "@/types/models/Assignment.interface";
import {
  draftButtonCss,
  footerLayoutCss,
  primaryBtnCss,
  secondaryBtnsCss,
} from "@/view/AssignModal/Footer/Footer.style";
import {
  ASSIGNMENT_MODE,
  currentAssignmentMode,
} from "@/view/utils/currentAssignmentMode";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface UnlevelledContentAssignModalActionsProps {
  onClickCancel: () => void;
  onClickCreate: () => void;
  onClickSaveChanges: () => void;
  onClickSaveDraft: () => void;
  updatedAssignment: Assignment;
}

export const UnlevelledContentAssignModalActions = ({
  onClickCancel,
  onClickCreate,
  onClickSaveChanges,
  onClickSaveDraft,
  updatedAssignment,
}: UnlevelledContentAssignModalActionsProps) => {
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { sm },
    },
  } = constants;
  const isMobile = windowWidth <= sm;
  const assignmentMode = currentAssignmentMode(updatedAssignment);
  const isCreateMode = assignmentMode === ASSIGNMENT_MODE.CREATE;

  const CancelButton = () => (
    <ButtonWithTrackEvent
      flavor={
        isCreateMode && isMobile
          ? Button.flavors.tertiary
          : Button.flavors.secondary
      }
      onClick={onClickCancel}
      objectType={objectTypes.assignment}
      actionPrefix={actionPrefixes.initiate}
      actionName={actionNames.cancel}
      dataProps={{
        "data-qa-selector": "genius-cancel-assignment-modal",
      }}
    >
      Cancel
    </ButtonWithTrackEvent>
  );

  const DraftButton = () => (
    <ButtonWithTrackEvent
      flavor={Button.flavors.secondary}
      objectType={objectTypes.assignment}
      actionName={isCreateMode ? actionNames.create : actionNames.update}
      onClick={onClickSaveDraft}
      dataProps={{
        "data-qa-selector":
          "genius-create-draft-assignment-in-assignment-modal",
      }}
      icon={{
        SvgComponent: FloppyHollowSVG,
        alignment: Button.iconAlignments.left,
      }}
      __cssFor={{ root: draftButtonCss }}
    >
      Save draft
    </ButtonWithTrackEvent>
  );

  const SaveButton = () => (
    <ButtonWithTrackEvent
      flavor={Button.flavors.primary}
      objectType={objectTypes.assignment}
      actionName={actionNames.create}
      onClick={onClickCreate}
      dataProps={{
        "data-qa-selector": "genius-create-assignment-in-assignment-modal",
      }}
    >
      Create assignment
    </ButtonWithTrackEvent>
  );

  const UpdateButton = () => (
    <ButtonWithTrackEvent
      flavor={Button.flavors.primary}
      objectType={objectTypes.assignment}
      actionName={actionNames.update}
      onClick={onClickSaveChanges}
      dataProps={{
        "data-qa-selector": "genius-update-assignment-in-assignment-modal",
      }}
    >
      Save changes
    </ButtonWithTrackEvent>
  );

  return (
    <div css={footerLayoutCss(isCreateMode)}>
      <div css={secondaryBtnsCss(isCreateMode)}>
        <CancelButton />
        {isCreateMode && <DraftButton />}
      </div>
      <div css={primaryBtnCss}>
        {isCreateMode ? <SaveButton /> : <UpdateButton />}
      </div>
    </div>
  );
};

export default UnlevelledContentAssignModalActions;
